import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiEye, FiEdit, FiSearch, FiDownload, FiCalendar } from 'react-icons/fi';
import { getOrders, updateOrderStatus, exportOrders } from '../services/orders';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import DateRangePicker from '../components/Common/DateRangePicker';
import toast from 'react-hot-toast';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  assigned: 'bg-purple-100 text-purple-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [filters, setFilters] = useState({ 
    status: '', 
    source: '', 
    search: '', 
    from_date: '', 
    to_date: '',
    page: 1, 
    limit: 20 
  });
  const [totalPages, setTotalPages] = useState(1);
  const [updating, setUpdating] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [filters]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await getOrders(filters);
      setOrders(data.data || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      await updateOrderStatus(orderId, newStatus, `Status updated to ${newStatus}`);
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(orders.map(o => o._id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedOrders.length === 0) {
      toast.error('No orders selected');
      return;
    }
    
    setUpdating('bulk');
    try {
      for (const orderId of selectedOrders) {
        await updateOrderStatus(orderId, newStatus, `Bulk status updated to ${newStatus}`);
      }
      toast.success(`${selectedOrders.length} orders updated`);
      setSelectedOrders([]);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update orders');
    } finally {
      setUpdating(null);
    }
  };

  // أضف function
const sendBulkReminder = async () => {
  if (selectedOrders.length === 0) {
    toast.error('No orders selected');
    return;
  }
  
  if (!window.confirm(`Send reminder to ${selectedOrders.length} customers?`)) return;
  
  setLoading(true);
  let successCount = 0;
  let failCount = 0;
  
  for (const orderId of selectedOrders) {
    try {
      await fetch(`/api/orders/${orderId}/reminder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      successCount++;
    } catch (error) {
      failCount++;
    }
  }
  
  toast.success(`Reminders sent: ${successCount} successful, ${failCount} failed`);
  setSelectedOrders([]);
  setLoading(false);
};


  
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const blob = await exportOrders({ ...filters, format });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders_${new Date().toISOString().split('T')[0]}.${format}`;
      a.click();
      toast.success(`Exported ${orders.length} orders`);
    } catch (error) {
      toast.error('Failed to export orders');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Orders</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting}
            className="flex items-center px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
          >
            <FiDownload className="mr-2" /> Export CSV
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            className="flex items-center px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
          >
            <FiDownload className="mr-2" /> Export PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">Source</label>
            <select
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="website">Website</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="google">Google</option>
              <option value="affiliate">Affiliate</option>
            </select>
          </div>
          
          <div className="lg:col-span-2">
            <label className="block text-sm text-gray-600 mb-1">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Order number or customer name/phone"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-2">
            <FiCalendar className="text-gray-400" />
            <div className="flex-1">
              <DateRangePicker
                fromDate={filters.from_date}
                toDate={filters.to_date}
                onFromChange={(date) => setFilters({ ...filters, from_date: date, page: 1 })}
                onToChange={(date) => setFilters({ ...filters, to_date: date, page: 1 })}
              />
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={() => setFilters({ status: '', source: '', search: '', from_date: '', to_date: '', page: 1, limit: 20 })}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedOrders.length > 0 && (
        <div className="bg-blue-50 rounded-lg p-3 mb-4 flex items-center justify-between">
          <span className="text-sm text-blue-700">{selectedOrders.length} orders selected</span>
          <div className="flex space-x-2">
            <select
              onChange={(e) => handleBulkStatusChange(e.target.value)}
              className="px-3 py-1 border border-blue-300 rounded-lg text-sm"
              defaultValue=""
            >
              <option value="" disabled>Bulk Update Status</option>
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

                              <button
  onClick={sendBulkReminder}
  className="px-3 py-1 bg-orange-500 text-white rounded-lg text-sm"
>
  Send Reminder
</button>
          </div>
        </div>
      )}

      {/* Orders Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={selectedOrders.length === orders.length && orders.length > 0}
                      className="rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order._id)}
                        onChange={() => handleSelectOrder(order._id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-primary-600">{order.order_number}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium">{order.customer?.name}</p>
                        <p className="text-sm text-gray-500">{order.customer?.phone}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm">
                        {order.items?.[0]?.service_name || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {formatDate(order.schedule?.date || order.created_at)}
                    </td>
                    <td className="px-6 py-4 font-medium">
                      ${order.total_amount}
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order._id, e.target.value)}
                        disabled={updating === order._id}
                        className={`px-2 py-1 rounded-full text-xs font-medium border-0 focus:ring-1 focus:ring-primary-500 ${statusColors[order.status]}`}
                      >
                        {statusOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-gray-100 rounded-full">
                        {order.source}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/orders/${order._id}`}
                        className="text-primary-600 hover:text-primary-800 transition"
                      >
                        <FiEye className="w-5 h-5 inline" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {orders.length === 0 && (
            <div className="text-center py-12 text-gray-500">No orders found</div>
          )}
          
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) => setFilters({ ...filters, page })}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
